<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useConfig } from 'dashboard/composables/useConfig';
import {
  getInboxClassByType,
  getInboxBrandColor,
  getInboxWarningIconClass,
} from 'dashboard/helper/inbox';

import SecondaryChildNavItem from './SecondaryChildNavItem.vue';
import {
  isOnMentionsView,
  isOnUnattendedView,
  isOnAwaitingReplyView,
} from '../../../store/modules/conversations/helpers/actionHelpers';
import Policy from '../../policy.vue';

export default {
  components: { SecondaryChildNavItem, Policy },
  props: {
    menuItem: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    const { isAdmin } = useAdmin();
    const { isEnterprise } = useConfig();
    return {
      isAdmin,
      isEnterprise,
    };
  },
  computed: {
    ...mapGetters({
      activeInbox: 'getSelectedInbox',
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      globalConfig: 'globalConfig/get',
    }),
    isCountZero() {
      return this.menuItem.count === 0;
    },
    isActiveView() {
      return this.computedClass.includes('active-view');
    },
    hasSubMenu() {
      return !!this.menuItem.children;
    },
    isMenuItemVisible() {
      if (this.menuItem.globalConfigFlag) {
        // this checks for the `csmlEditorHost` flag in the global config
        // if this is present, we toggle the CSML editor menu item
        // TODO: This is very specific, and can be handled better, fix it
        return !!this.globalConfig[this.menuItem.globalConfigFlag];
      }

      let isFeatureEnabled = true;
      if (this.menuItem.featureFlag) {
        isFeatureEnabled = this.isFeatureEnabledonAccount(
          this.accountId,
          this.menuItem.featureFlag
        );
      }

      if (this.menuItem.isEnterpriseOnly) {
        if (!this.isEnterprise) return false;
        return isFeatureEnabled || this.globalConfig.displayManifest;
      }

      if (this.menuItem.featureFlag) {
        return this.isFeatureEnabledonAccount(
          this.accountId,
          this.menuItem.featureFlag
        );
      }

      return isFeatureEnabled;
    },
    isAllConversations() {
      return (
        this.$store.state.route.name === 'inbox_conversation' &&
        this.menuItem.toStateName === 'home'
      );
    },
    isMentions() {
      return (
        isOnMentionsView({ route: this.$route }) &&
        this.menuItem.toStateName === 'conversation_mentions'
      );
    },
    isUnattended() {
      return (
        isOnUnattendedView({ route: this.$route }) &&
        this.menuItem.toStateName === 'conversation_unattended'
      );
    },
    isAwaitingReply() {
      return (
        isOnAwaitingReplyView({ route: this.$route }) &&
        this.menuItem.toStateName === 'conversation_awaiting_reply'
      );
    },
    isTeamsSettings() {
      return (
        this.$store.state.route.name === 'settings_teams_edit' &&
        this.menuItem.toStateName === 'settings_teams_list'
      );
    },
    isInboxSettings() {
      return (
        this.$store.state.route.name === 'settings_inbox_show' &&
        this.menuItem.toStateName === 'settings_inbox_list'
      );
    },
    isIntegrationsSettings() {
      return (
        this.$store.state.route.name === 'settings_integrations_webhook' &&
        this.menuItem.toStateName === 'settings_integrations'
      );
    },
    isApplicationsSettings() {
      return (
        this.$store.state.route.name === 'settings_applications_integration' &&
        this.menuItem.toStateName === 'settings_applications'
      );
    },
    isCurrentRoute() {
      return this.$store.state.route.name.includes(this.menuItem.toStateName);
    },

    computedClass() {
      // If active inbox is present, do not highlight conversations
      if (this.activeInbox) return ' ';
      if (
        this.isAllConversations ||
        this.isMentions ||
        this.isUnattended ||
        this.isAwaitingReply ||
        this.isCurrentRoute
      ) {
        return 'bg-woot-25 dark:bg-slate-800 text-woot-500 dark:text-woot-500 hover:text-woot-500 dark:hover:text-woot-500 active-view';
      }
      if (this.hasSubMenu) {
        if (
          this.isTeamsSettings ||
          this.isInboxSettings ||
          this.isIntegrationsSettings ||
          this.isApplicationsSettings
        ) {
          return 'bg-woot-25 dark:bg-slate-800 text-woot-500 dark:text-woot-500 hover:text-woot-500 dark:hover:text-woot-500 active-view';
        }
        return 'hover:text-slate-700 dark:hover:text-slate-100';
      }

      return 'hover:text-slate-700 dark:hover:text-slate-100';
    },
  },
  methods: {
    async hideInbox(inboxId) {
      try {
        await this.$store.dispatch('inboxes/updateInbox', {
          id: inboxId,
          formData: false,
          hidden_in_sidebar: true,
        });
        useAlert(this.$t('SIDEBAR.INBOX_HIDDEN'));
      } catch (error) {
        useAlert(this.$t('SIDEBAR.INBOX_HIDE_ERROR'));
      }
    },
    inboxBrandColor(child) {
      if (!child.type) return '';
      return (
        getInboxBrandColor(child.type, child.phoneNumber, child.webhookUrl) ||
        ''
      );
    },
    computedInboxClass(child) {
      const { type, phoneNumber, webhookUrl } = child;
      if (!type) return '';
      const classByType = getInboxClassByType(type, phoneNumber, webhookUrl);
      return classByType;
    },
    computedInboxErrorClass(child) {
      const { type, reauthorizationRequired } = child;
      if (!type) return '';
      const warningClass = getInboxWarningIconClass(
        type,
        reauthorizationRequired
      );
      return warningClass;
    },
    newLinkClick(e, navigate) {
      if (this.menuItem.newLinkRouteName) {
        navigate(e);
      } else if (this.menuItem.showModalForNewItem) {
        if (this.menuItem.modalName === 'AddLabel') {
          e.preventDefault();
          this.$emit('addLabel');
        }
      }
    },
    showItem(item) {
      return this.isAdmin && !!item.newLink;
    },
    onClickOpen() {
      this.$emit('open');
    },
    showChildCount(count) {
      return Number.isInteger(count);
    },
  },
};
</script>

<template>
  <li v-show="isMenuItemVisible" class="mt-1">
    <div
      v-if="hasSubMenu"
      class="flex items-center justify-between mt-3 pt-3 border-0 border-t border-solid border-slate-50 dark:border-white/[0.05]"
    >
      <span
        class="flex items-center min-w-0 gap-1.5 px-2 py-1 my-0 text-[11px] font-semibold tracking-wider uppercase whitespace-nowrap text-slate-400 dark:text-slate-500"
      >
        <fluent-icon
          v-if="menuItem.icon"
          :icon="menuItem.icon"
          size="13"
          class="opacity-70"
        />
        <span class="truncate">{{ $t(`SIDEBAR.${menuItem.label}`) }}</span>
      </span>
      <div v-if="menuItem.showNewButton" class="flex items-center">
        <woot-button
          size="tiny"
          variant="clear"
          color-scheme="secondary"
          icon="add"
          class="p-0 ml-2"
          @click="onClickOpen"
        />
      </div>
    </div>
    <router-link
      v-else
      class="cs-nav-item flex items-center p-2 m-0 text-sm font-medium leading-4 rounded-lg text-slate-700 dark:text-slate-300 transition-all duration-150 hover:bg-woot-50 dark:hover:bg-woot-500/10 hover:ring-1 hover:ring-inset hover:ring-woot-100 dark:hover:ring-woot-500/20 dark:hover:text-white"
      :class="computedClass"
      :to="menuItem && menuItem.toState"
    >
      <fluent-icon
        :icon="menuItem.icon"
        class="min-w-[1rem] mr-1.5 rtl:mr-0 rtl:ml-1.5"
        size="14"
      />
      <span class="truncate" :title="$t(`SIDEBAR.${menuItem.label}`)">
        {{ $t(`SIDEBAR.${menuItem.label}`) }}
      </span>
      <fluent-icon
        v-if="menuItem.tooltip"
        v-tooltip.right="$t(`SIDEBAR.${menuItem.tooltip}`)"
        icon="info"
        size="12"
        class="flex-shrink-0 ml-1 rtl:ml-0 rtl:mr-1 text-slate-400 dark:text-slate-500"
      />
      <span
        v-if="showChildCount(menuItem.count)"
        class="px-1 py-0 mx-1 font-medium rounded-md text-xxs"
        :class="{
          'text-slate-300 dark:text-slate-600': isCountZero && !isActiveView,
          'text-slate-600 dark:text-slate-50': !isCountZero && !isActiveView,
          'bg-woot-75 dark:bg-woot-200 text-woot-600 dark:text-woot-600':
            isActiveView,
          'bg-slate-50 dark:bg-slate-700': !isActiveView,
        }"
      >
        {{ `${menuItem.count}` }}
      </span>
      <span
        v-if="menuItem.beta"
        data-view-component="true"
        label="Beta"
        class="inline-block px-1 mx-1 font-medium leading-4 text-green-500 border border-green-400 rounded-lg text-xxs"
      >
        {{ $t('SIDEBAR.BETA') }}
      </span>
    </router-link>

    <ul v-if="hasSubMenu" class="mb-0 mt-0.5 ml-0 list-none ltr:pl-2 rtl:pr-2">
      <SecondaryChildNavItem
        v-for="child in menuItem.children"
        :key="child.id"
        :to="child.toState"
        :label="child.label"
        :label-color="child.color"
        :should-truncate="child.truncateLabel"
        :icon="computedInboxClass(child)"
        :icon-color-override="inboxBrandColor(child)"
        :warning-icon="computedInboxErrorClass(child)"
        :show-child-count="showChildCount(child.count)"
        :child-item-count="child.count"
        :hideable="!!child.hideable"
        @hide="hideInbox(child.id)"
      />
      <Policy :permissions="['administrator']">
        <router-link
          v-if="menuItem.newLink"
          v-slot="{ href, navigate }"
          :to="menuItem.toState"
          custom
        >
          <li>
            <!-- Mismo formato que los ítems de la lista: el woot-button
                 tiny se desbordaba del sidebar y parecía roto. -->
            <a
              :href="href"
              :data-testid="menuItem.dataTestid"
              class="flex items-center min-w-0 gap-1.5 px-2 py-1.5 mt-0.5 text-xs rounded-md text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800"
              @click="e => newLinkClick(e, navigate)"
            >
              <fluent-icon icon="add" size="12" class="flex-shrink-0" />
              <span class="truncate">
                {{ $t(`SIDEBAR.${menuItem.newLinkTag}`) }}
              </span>
            </a>
          </li>
        </router-link>
      </Policy>
    </ul>
  </li>
</template>

<style lang="scss" scoped>
// Chatsell: ítem activo del sidebar. El azul se reserva para la navegación activa.
// Indicador vertical de 3px vía inset box-shadow (sin layout shift).
.cs-nav-item.active-view {
  color: var(--cs-blue) !important;
  background: var(--cs-blue-soft) !important;
  box-shadow: inset 3px 0 0 0 var(--cs-blue);
}

// El ring de hover es un box-shadow y competiría con el indicador de arriba,
// así que sobre el ítem activo se conserva solo la barra azul.
.cs-nav-item.active-view:hover {
  box-shadow: inset 3px 0 0 0 var(--cs-blue) !important;
}
</style>
